import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IntegrationProvider } from "@/hooks/useIntegrations";
import { WebhookInstructions } from "./WebhookInstructions";
import { useProduct } from "@/contexts/ProductContext";

interface IntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: IntegrationProvider;
  onConnect: (provider: IntegrationProvider, credentials: any, productId?: string) => void;
}

const providerFields: Record<
  IntegrationProvider,
  Array<{ name: string; label: string; type: string; placeholder: string; required: boolean }>
> = {
  stripe: [
    { name: "api_key", label: "API Key", type: "password", placeholder: "sk_live_...", required: true },
    { name: "webhook_secret", label: "Webhook Secret", type: "password", placeholder: "whsec_...", required: false },
  ],
  paypal: [
    { name: "client_id", label: "Client ID", type: "text", placeholder: "Client ID", required: true },
    { name: "client_secret", label: "Client Secret", type: "password", placeholder: "Client Secret", required: true },
    { name: "mode", label: "Modo", type: "select", placeholder: "sandbox", required: true },
  ],
  mercadopago: [
    { name: "access_token", label: "Access Token", type: "password", placeholder: "APP_USR-...", required: true },
  ],
  pagseguro: [
    { name: "email", label: "Email", type: "email", placeholder: "seu@email.com", required: true },
    { name: "token", label: "Token", type: "password", placeholder: "Token", required: true },
  ],
  hotmart: [
    { name: "client_id", label: "Client ID", type: "text", placeholder: "Client ID", required: true },
    { name: "client_secret", label: "Client Secret", type: "password", placeholder: "Client Secret", required: true },
    { name: "basic_token", label: "Basic Token", type: "password", placeholder: "Basic Token", required: true },
  ],
  kiwify: [
    { name: "api_key", label: "API Key", type: "password", placeholder: "API Key", required: true },
  ],
  pagarme: [
    { name: "api_key", label: "API Key", type: "password", placeholder: "sk_...", required: true },
  ],
  vindi: [
    { name: "api_key", label: "API Key", type: "password", placeholder: "API Key", required: true },
  ],
  iugu: [
    { name: "api_token", label: "API Token", type: "password", placeholder: "API Token", required: true },
  ],
  eduzz: [
    { name: "api_key", label: "API Key", type: "password", placeholder: "API Key", required: true },
    { name: "public_key", label: "Public Key", type: "text", placeholder: "Public Key", required: true },
  ],
};

export function IntegrationDialog({
  open,
  onOpenChange,
  provider,
  onConnect,
}: IntegrationDialogProps) {
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { selectedProduct } = useProduct();

  const fields = providerFields[provider] || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onConnect(provider, credentials, selectedProduct?.id);
      onOpenChange(false);
      setCredentials({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (name: string, value: string) => {
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurar {provider.charAt(0).toUpperCase() + provider.slice(1)}</DialogTitle>
          <DialogDescription>
            Insira suas credenciais para conectar a integração
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="credentials" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="credentials">Credenciais</TabsTrigger>
            <TabsTrigger value="webhook">Webhook</TabsTrigger>
          </TabsList>

          <TabsContent value="credentials">
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              {fields.map((field) => (
                <div key={field.name} className="space-y-2">
                  <Label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  {field.type === "select" ? (
                    <select
                      id={field.name}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={credentials[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                    >
                      <option value="">Selecione...</option>
                      <option value="sandbox">Sandbox</option>
                      <option value="live">Live</option>
                    </select>
                  ) : (
                    <Input
                      id={field.name}
                      type={field.type}
                      placeholder={field.placeholder}
                      value={credentials[field.name] || ""}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                    />
                  )}
                </div>
              ))}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isLoading} className="gradient-primary">
                  {isLoading ? "Conectando..." : "Conectar"}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="webhook">
            <WebhookInstructions provider={provider} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
